import { useState } from "react"
import { useLocalization } from "@tm/localization"
import { StatusSwitch } from "@tm/controls"
import { Button, buttonClasses, Icon, Paper, Stack, styled, Typography } from "@tm/components"
import { ReturnItem } from "../../../data/model"
import RemoveItemsDialog from "../../_shared/RemoveItemsDialog"

type Props = {
    items: number
    articles: number
    allSelected: boolean
    equalReturnStatus: boolean
    selectedPartsIncludedInOrder: boolean
    selectedPartsBeingUpdated: boolean
    selectedParts: Array<ReturnItem>
    onSelectAll(): void
    onUnselectAll(): void
    onRemoveReturnItems(ids: Array<ReturnItem>): void
    onUpdateItemsReturnStatus(includeInReturn: boolean): void
}

const SelectionPaper = styled(Paper)(({ theme }) => ({
    position: "fixed",
    zIndex: 5,
    padding: "0 0 0 12px",
    right: "26px",
    marginTop: "4px",
    boxShadow: "0 4px 20px 0 rgba(0, 0, 0, 0.54)",
    border: `1px solid ${theme?.colors?.primary || "#82c4f8"}`,
    borderRadius: "3px",
}))

const CancelButton = styled(Button)(({ theme }) => ({
    [`&.${buttonClasses.contained}`]: {
        height: "42px",
        width: "56px",
        backgroundColor: theme.palette.grey[800],
        color: "#fff",
        marginLeft: "8px",
        borderBottomLeftRadius: "0 !important",
        borderTopLeftRadius: "0 !important",
    },
}))

export default function PartsSelectionComponent({
    articles,
    allSelected,
    equalReturnStatus,
    items,
    selectedParts,
    selectedPartsBeingUpdated,
    selectedPartsIncludedInOrder,
    onRemoveReturnItems,
    onSelectAll,
    onUnselectAll,
    onUpdateItemsReturnStatus,
}: Props) {
    const { translateText } = useLocalization()
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)

    function handlRemoveReturnItems() {
        onRemoveReturnItems(selectedParts)
    }

    function handleRemoveAll() {
        setShowDeleteDialog(true)
    }

    function handleOrderStatusSwitchChange() {
        onUpdateItemsReturnStatus(!selectedPartsIncludedInOrder)
    }

    function handleSelectButtonClick() {
        if (allSelected) {
            onUnselectAll()
        } else {
            onSelectAll()
        }
    }

    function handleCancelButtonClick() {
        onUnselectAll()
    }

    function closeDeleteDialog() {
        setShowDeleteDialog(false)
    }

    let status: "checked" | "checked-not-equal" | "unchecked" | "unchecked-not-equal" = "unchecked"
    if (selectedPartsIncludedInOrder && equalReturnStatus) {
        status = "checked"
    } else if (selectedPartsIncludedInOrder && !equalReturnStatus) {
        status = "checked-not-equal"
    } else if (!selectedPartsIncludedInOrder && equalReturnStatus) {
        status = "unchecked"
    } else if (!selectedPartsIncludedInOrder && !equalReturnStatus) {
        status = "unchecked-not-equal"
    }

    const notOrderableParts = selectedParts.some((part) => !part.returnReasonId || (part.isReturnInfoRequired && !part.returnInfo) || !part.canReturn)

    return (
        <SelectionPaper>
            <Stack direction="row" gap={2} justifyContent="space-between" alignItems="center">
                <Stack direction="row" gap={0.5}>
                    <Typography variant="body2">{translateText(701)}:</Typography>
                    <Typography variant="body2">
                        {items} {translateText(479)},
                    </Typography>
                    <Typography variant="body2">
                        {articles} {translateText(90)}
                    </Typography>
                </Stack>
                <Button onClick={handleSelectButtonClick}>{!allSelected ? translateText(702) : translateText(703)}</Button>
                <StatusSwitch
                    label={translateText(1515)}
                    alignLabel="left"
                    status={status}
                    disabled={selectedPartsBeingUpdated || notOrderableParts}
                    onChange={handleOrderStatusSwitchChange}
                />
                <Button startIcon={<Icon name="delete" />} onClick={handleRemoveAll} />
                <RemoveItemsDialog
                    dialogTextId={834}
                    showDeleteDialog={showDeleteDialog}
                    onCloseRemoveDialog={closeDeleteDialog}
                    onRemoveReturnItems={handlRemoveReturnItems}
                />

                <CancelButton startIcon={<Icon name="cancel-x" />} title={translateText(317)} onClick={handleCancelButtonClick} />
            </Stack>
        </SelectionPaper>
    )
}
